import { useContext, useEffect, useRef, useState } from "react";
import { cn, compararTexto } from "@/lib/utils";

import { DataTable } from "@/components/data-table";
import { useGlobalLoader } from "@/components/GlobalLoader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertTriangle,
  Check,
  ChevronsUpDown,
  Eye,
  FileSpreadsheet,
  Layers,
  MapPin,
  Radio,
  RefreshCw,
  Tag as TagIcon,
  Upload,
  X,
} from "lucide-react";

import { useCategorias } from "@/contexts/CategoriasContext";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import { useLocalizacoes } from "@/contexts/LocalizacoesContext";
import { useEpc } from "@/contexts/EpcContext";
import { useImpressao } from "@/components/ImpressaoComponent";
import { MODOS_IMPRESSAO } from "@/lib/modosImpressao";
import { useSeriais } from "@/contexts/SeriaisContext";
import { useItem } from "@/contexts/ItemContext";
import { useContaConfig } from "@/contexts/ContaConfigContext";
import { aplicarPreDefinicoesUnicas } from "@/components/InfoComplField";
import { AdminContext } from "@/contexts/AdminContext";

import { type CategoriaItemResponse } from "@/types/categoria-item-response";
import type { ImpressaoItem } from "@/types/impressao-item";

import {
  categoriaComboValue,
  verificarEpcsInvalidos,
} from "@/utils/UtilFuncoes";
import { LocalizacaoCombobox } from "@/components/LocalizacaoCombobox";
import { alert } from "@/contexts/AlertModalContext";

const ZPL_EM_BRANCO = "^XA\n^FO1,1\n^GB1,1,1^FS\n^XZ";

function GravacaoAvulsa() {
  const { contaConfig, numeroDeImpressoes } = useContaConfig();
  const { adicionarItensNaFila } = useImpressao();
  const { isLoading } = useGlobalLoader();
  const { gerarEpcs } = useEpc();
  const { localizacoes, coletaLocalizacoes } = useLocalizacoes();
  const { impressaoConfig, coletarBlocosEPC } = useImpressaoConfig();
  const { categorias, coletaCategorias } = useCategorias();
  const { coletaSeriais, adicionarMaisUmNosSeriais } = useSeriais();
  const { conferirTagsCadastradas } = useItem();
  const { requireAdmin } = useContext(AdminContext)!;

  const [modo, setModo] = useState<"categoria" | "planilha">("categoria");
  const [inicioTagVirgem, setInicioTagVirgem] = useState("E280");

  // ---- Estados comuns ----
  const [categoriaSelecionada, setCategoriaSelecionada] = useState<
    CategoriaItemResponse | undefined
  >(undefined);
  const [comboCodigo, setComboCodigo] = useState(false);
  const [localizacaoSelecionada, setLocalizacaoSelecionada] = useState("");
  const [localizacaoSelecionada2, setLocalizacaoSelecionada2] = useState("");
  const [localizacaoSelecionada3, setLocalizacaoSelecionada3] = useState("");
  const [localizacaoSelecionada4, setLocalizacaoSelecionada4] = useState("");
  const [permitirItensRepetidos, setPermitirItensRepetidos] = useState(false);

  // ---- Modo categoria ----
  const [quantidade, setQuantidade] = useState(1);

  // ---- Modo planilha ----
  const [epcsDaPlanilha, setEpcsDaPlanilha] = useState<string[]>([]);
  const [fileName, setFileName] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ---- Dialog repetidos ----
  const [epcRepetidosDialog, setEpcRepetidosDialog] = useState(false);
  const [epcRepetidosEncontrados, setEpcRepetidosEncontrados] = useState<
    Array<{ epc: string; codigo: string }>
  >([]);

  // ---- Preferência persistida ----
  useEffect(() => {
    const repetidos = localStorage.getItem("permitirItensRepetidos");
    setPermitirItensRepetidos(repetidos === "true");
    const inicioSalvo = localStorage.getItem("gravacaoAvulsa.inicioTagVirgem");
    if (inicioSalvo) setInicioTagVirgem(inicioSalvo);
  }, []);

  useEffect(() => {
    if (
      impressaoConfig?.inicio_tag_virgem &&
      !localStorage.getItem("gravacaoAvulsa.inicioTagVirgem")
    ) {
      setInicioTagVirgem(impressaoConfig.inicio_tag_virgem);
    }
  }, [impressaoConfig?.inicio_tag_virgem]);

  // ---- Definir localização padrão ----
  useEffect(() => {
    if (impressaoConfig && localizacoes.length > 0) {
      const n1 = localizacoes.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao,
      );
      setLocalizacaoSelecionada(n1?._id || "");
      const n2 = n1?.children?.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao_2,
      );
      setLocalizacaoSelecionada2(n2?._id || "");
      const n3 = n2?.children?.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao_3,
      );
      setLocalizacaoSelecionada3(n3?._id || "");
      const n4 = n3?.children?.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao_4,
      );
      setLocalizacaoSelecionada4(n4?._id || "");
    }
  }, [impressaoConfig, localizacoes]);

  const sincronizar = async () => {
    await coletaCategorias();
    await coletaSeriais();
    await coletaLocalizacoes();
    setCategoriaSelecionada(undefined);
    alert.success("Dados sincronizados com sucesso!");
  };

  // ---- Upload planilha ----
  const processarArquivo = (file: File) => {
    const nome = file.name.toLowerCase();
    if (!nome.endsWith(".csv") && !nome.endsWith(".txt")) {
      alert.error("Apenas arquivos .csv ou .txt são aceitos.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const conteudo = (event.target?.result as string) || "";
      // Aceita: 1 EPC por linha (txt) ou CSV com coluna TAG (planilha exportada).
      // Se a 1ª linha for "TAG", trata como CSV; senão, cada linha é um EPC.
      const linhas = conteudo
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter((l) => l.length > 0);

      if (linhas.length === 0) {
        alert.error("Arquivo vazio.");
        return;
      }

      let epcs: string[] = [];
      const primeira = linhas[0].toUpperCase().replace(/['"]/g, "");
      if (primeira === "TAG" || primeira.split(";")[0] === "TAG") {
        // CSV com cabeçalho — coluna TAG
        const cabecalhos = linhas[0]
          .split(";")
          .map((h) => h.trim().replace(/['"]/g, "").toUpperCase());
        const idxTag = cabecalhos.indexOf("TAG");
        if (idxTag === -1) {
          alert.error("Coluna TAG não encontrada no CSV.");
          return;
        }
        for (let i = 1; i < linhas.length; i++) {
          const valor = (linhas[i].split(";")[idxTag] || "")
            .trim()
            .replace(/['"]/g, "");
          if (valor) epcs.push(valor);
        }
      } else {
        // 1 EPC por linha
        epcs = linhas.map((l) => l.replace(/['"]/g, "").trim());
      }

      // Padroniza pra 24 chars (mesmo padrão do ImportarPlanilha)
      epcs = epcs.map((e) => e.toUpperCase().padStart(24, "0"));

      if (epcs.length === 0) {
        alert.error("Nenhum EPC encontrado no arquivo.");
        return;
      }

      setEpcsDaPlanilha(epcs);
      setFileName(file.name);
      alert.success(`${epcs.length} EPC(s) importado(s).`);
    };
    reader.readAsText(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processarArquivo(e.target.files[0]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (epcsDaPlanilha.length > 0 || isLoading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) processarArquivo(file);
  };

  const limparPlanilha = () => {
    setEpcsDaPlanilha([]);
    setFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ---- Validação do início da tag virgem ----
  const inicioTagVirgemValido = /^[0-9A-Fa-f]{1,24}$/.test(inicioTagVirgem);

  // ---- Gerar lista para gravação ----
  const gerarListaParaGravacao = async () => {
    if (!impressaoConfig) {
      alert.error("Configuração de impressão não encontrada.");
      return;
    }
    if (!impressaoConfig.app_id) {
      alert.error(
        "Informe o Hardware ID nas configurações para realizar a gravação.",
      );
      return;
    }
    if (!inicioTagVirgemValido) {
      alert.error(
        "Início da tag virgem inválido — informe um valor hexadecimal (ex: E280).",
      );
      return;
    }
    if (!categoriaSelecionada) {
      alert.error("Selecione uma categoria para vincular as etiquetas.");
      return;
    }
    if (!localizacaoSelecionada) {
      alert.error("Selecione uma localização para vincular as etiquetas.");
      return;
    }

    localStorage.setItem("gravacaoAvulsa.inicioTagVirgem", inicioTagVirgem);

    let listaEpcs: { epc: string; zpl: string }[] = [];

    if (modo === "categoria") {
      if (quantidade <= 0) {
        alert.error("Quantidade inválida.");
        return;
      }
      const localizacoesZpl: string[] = [
        localizacoes.find((loc) => loc._id === localizacaoSelecionada)
          ?.descricao || "",
        localizacoes
          .find((n1) => n1._id === localizacaoSelecionada)
          ?.children.find((loc) => loc._id === localizacaoSelecionada2)
          ?.descricao || "",
        localizacoes
          .find((n1) => n1._id === localizacaoSelecionada)
          ?.children.find((n2) => n2._id === localizacaoSelecionada2)
          ?.children.find((loc) => loc._id === localizacaoSelecionada3)
          ?.descricao || "",
        localizacoes
          .find((n1) => n1._id === localizacaoSelecionada)
          ?.children.find((n2) => n2._id === localizacaoSelecionada2)
          ?.children.find((n3) => n3._id === localizacaoSelecionada3)
          ?.children.find((loc) => loc._id === localizacaoSelecionada4)
          ?.descricao || "",
      ];
      const gerados = gerarEpcs(
        categoriaSelecionada,
        quantidade,
        localizacoesZpl,
        coletarBlocosEPC(),
      );
      listaEpcs = gerados.map((g) => ({ epc: g.epc, zpl: ZPL_EM_BRANCO }));
    } else {
      if (epcsDaPlanilha.length === 0) {
        alert.error("Importe uma planilha com EPCs para gravar.");
        return;
      }
      listaEpcs = epcsDaPlanilha.map((epc) => ({ epc, zpl: ZPL_EM_BRANCO }));
    }

    if (listaEpcs.length === 0) {
      alert.error("Nenhum EPC para gravar.");
      return;
    }

    const epcs_para_gravacao = listaEpcs.map((i) => i.epc);
    if (verificarEpcsInvalidos(epcs_para_gravacao)) {
      alert.error(
        "EPCs inválidos encontrados. Verifique os dados e tente novamente.",
      );
      return;
    }

    const numero_de_impressoes = numeroDeImpressoes || 0;

    // Info. complementar vazia na categoria cai na pré-definição do campo
    // quando ela tem uma única opção cadastrada (não há o que escolher).
    const listaParaGravacao: ImpressaoItem[] = listaEpcs.map((item, index) =>
      aplicarPreDefinicoesUnicas(impressaoConfig, {
        _id: undefined,
        inf_compl_1: categoriaSelecionada.inf_compl_1 || "",
        inf_compl_2: categoriaSelecionada.inf_compl_2 || "",
        inf_compl_3: categoriaSelecionada.inf_compl_3 || "",
        inf_compl_4: categoriaSelecionada.inf_compl_4 || "",
        inf_compl_5: categoriaSelecionada.inf_compl_5 || "",
        inf_compl_6: categoriaSelecionada.inf_compl_6 || "",
        inf_compl_7: categoriaSelecionada.inf_compl_7 || "",
        inf_compl_8: categoriaSelecionada.inf_compl_8 || "",
        inf_compl_9: categoriaSelecionada.inf_compl_9 || "",
        inf_compl_10: categoriaSelecionada.inf_compl_10 || "",
        inf_compl_11: categoriaSelecionada.inf_compl_11 || "",
        inf_compl_12: categoriaSelecionada.inf_compl_12 || "",
        tag: item.epc,
        codigo: categoriaSelecionada.ean,
        descricao: categoriaSelecionada.descricao,
        zpl_impressao: item.zpl,
        id_categoria: categoriaSelecionada._id,
        id_conta: impressaoConfig.id_conta,
        id_usuario: impressaoConfig.id_usuario,
        id_localizacao1: localizacaoSelecionada,
        id_localizacao2: localizacaoSelecionada2,
        id_localizacao3: localizacaoSelecionada3,
        id_localizacao4: localizacaoSelecionada4,
        valor: 0,
        quantidade: "",
        dt_validade: "",
        n_impressao: (numero_de_impressoes + index + 1).toString(),
        id_externo: "",
        ultima_localizacao: {
          primeira_vez: true,
          verificar: contaConfig?.transferencia_loc_origem || false,
        },
        inicio_tag_virgem_override: inicioTagVirgem.toUpperCase(),
        pular_marcacao_defeito_override: true,
      }),
    );

    if (!permitirItensRepetidos) {
      const itensNaBase = await conferirTagsCadastradas(
        listaParaGravacao.map((item) => item.tag),
      );
      if (itensNaBase.length > 0) {
        const repetidosFormatados = listaParaGravacao
          .filter((item) => itensNaBase.some((i) => i.tag === item.tag))
          .map((item) => ({ epc: item.tag, codigo: item.codigo }));
        setEpcRepetidosEncontrados(repetidosFormatados);
        setEpcRepetidosDialog(true);
        adicionarMaisUmNosSeriais(
          repetidosFormatados.map((item) => item.codigo),
        );
        return;
      }
    }

    adicionarItensNaFila(
      listaParaGravacao,
      undefined,
      undefined,
      MODOS_IMPRESSAO.GRAVACAO_AVULSA,
    );

    // Limpa para próxima sessão
    if (modo === "planilha") limparPlanilha();
    setCategoriaSelecionada(undefined);
  };

  if (!impressaoConfig) return null;

  const totalParaGravar =
    modo === "categoria" ? quantidade : epcsDaPlanilha.length;
  const podeGravar =
    !!categoriaSelecionada &&
    !!localizacaoSelecionada &&
    inicioTagVirgemValido &&
    totalParaGravar > 0;
  const categoriasOrdenadas = [...categorias].sort((a, b) =>
    compararTexto(a.descricao ?? "", b.descricao ?? ""),
  );

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight">
            Gravação Avulsa
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Grave etiquetas RFID em tags virgens sem imprimir nada
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-md border bg-card px-3 h-9">
            <Switch
              id="gravacao-avulsa-repetidos"
              disabled={isLoading}
              checked={permitirItensRepetidos}
              onCheckedChange={(checked) => {
                requireAdmin(() => {
                  setPermitirItensRepetidos(checked);
                  localStorage.setItem(
                    "permitirItensRepetidos",
                    String(checked),
                  );
                });
              }}
            />
            <Label
              htmlFor="gravacao-avulsa-repetidos"
              className="text-xs cursor-pointer"
            >
              Permitir repetidos
            </Label>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={sincronizar}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            <span className="hidden sm:inline">Sincronizar</span>
          </Button>

          <Button
            size="sm"
            onClick={gerarListaParaGravacao}
            disabled={!podeGravar || isLoading}
            title={
              !podeGravar
                ? "Preencha início, categoria, localização e EPCs"
                : `Gravar ${totalParaGravar} etiqueta${totalParaGravar === 1 ? "" : "s"}`
            }
          >
            <Radio className="h-4 w-4" />
            <span className="hidden sm:inline">Gravar</span>
            {podeGravar && (
              <Badge
                variant="secondary"
                className="ml-1 h-5 px-1.5 text-[10px] bg-white/20 text-primary-foreground hover:bg-white/20"
              >
                {totalParaGravar}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* 1. Início da tag virgem */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
              1
            </div>
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <TagIcon className="h-4 w-4 text-muted-foreground" />
              Início da tag a ser gravada
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 sm:max-w-xs">
            <Label
              htmlFor="gravacao-avulsa-inicio"
              className="text-xs text-muted-foreground"
            >
              Prefixo que identifica uma tag a ser gravada (ex: E280)
            </Label>
            <Input
              autoComplete="off"
              id="gravacao-avulsa-inicio"
              type="text"
              value={inicioTagVirgem}
              onChange={(e) =>
                setInicioTagVirgem(
                  e.target.value.toUpperCase().replace(/[^0-9A-F]/g, ""),
                )
              }
              placeholder="E280"
              className={cn(!inicioTagVirgemValido && "border-destructive")}
              required
            />
            {!inicioTagVirgemValido && (
              <span className="text-[11px] text-destructive">
                Informe um valor hexadecimal (0-9, A-F).
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 2. Modo */}
      <Tabs
        value={modo}
        onValueChange={(v) => setModo(v as "categoria" | "planilha")}
      >
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                  2
                </div>
                <CardTitle className="text-sm font-medium">
                  Origem dos EPCs
                </CardTitle>
              </div>
              <TabsList>
                <TabsTrigger value="categoria">
                  <TagIcon className="h-3.5 w-3.5" />
                  Categoria + quantidade
                </TabsTrigger>
                <TabsTrigger value="planilha">
                  <FileSpreadsheet className="h-3.5 w-3.5" />
                  Planilha de EPCs
                </TabsTrigger>
              </TabsList>
            </div>
          </CardHeader>
          <CardContent>
            <TabsContent value="categoria" className="mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-3">
                <div className="grid gap-2">
                  <Label>
                    Categoria <span className="text-destructive">*</span>
                  </Label>
                  <Popover open={comboCodigo} onOpenChange={setComboCodigo}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="justify-between font-normal"
                      >
                        {categoriaSelecionada ? (
                          <span className="truncate">
                            <span className="font-medium">
                              {categoriaSelecionada.descricao}
                            </span>
                            <span className="text-muted-foreground ml-2">
                              {categoriaSelecionada.ean}
                            </span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">
                            Selecione uma categoria
                          </span>
                        )}
                        <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="p-0 w-(--radix-popover-trigger-width)"
                    >
                      <Command className="rounded-lg">
                        <CommandInput placeholder="Buscar por descrição, código ou info. complementar..." />
                        <CommandList>
                          <CommandEmpty>
                            <div className="text-xs text-muted-foreground p-2">
                              Nenhuma categoria encontrada
                            </div>
                          </CommandEmpty>
                          <CommandGroup>
                            {categoriasOrdenadas.map((categoria, index) => (
                              <CommandItem
                                key={index}
                                value={categoriaComboValue(categoria)}
                                onSelect={(currentValue) => {
                                  const ean = currentValue
                                    .split("|-|")[0]
                                    .trim();
                                  const sel = categorias.find(
                                    (c) => c.ean === ean,
                                  );
                                  setCategoriaSelecionada(sel);
                                  setComboCodigo(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    categoriaSelecionada?.ean === categoria.ean
                                      ? "opacity-100"
                                      : "opacity-0",
                                  )}
                                />
                                <div className="flex flex-col min-w-0">
                                  <span className="text-sm font-medium truncate">
                                    {categoria.descricao}
                                  </span>
                                  <span className="text-[11px] text-muted-foreground">
                                    {categoria.ean}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="gravacao-avulsa-quantidade">
                    Quantidade <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    autoComplete="off"
                    min={1}
                    value={quantidade}
                    disabled={isLoading}
                    id="gravacao-avulsa-quantidade"
                    type="number"
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setQuantidade(!isNaN(v) && v > 0 ? v : 1);
                    }}
                    required
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="planilha" className="mt-0">
              <div className="grid gap-3">
                <div className="grid gap-2">
                  <Label>
                    Categoria <span className="text-destructive">*</span>
                  </Label>
                  <Popover open={comboCodigo} onOpenChange={setComboCodigo}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="justify-between font-normal"
                      >
                        {categoriaSelecionada ? (
                          <span className="truncate">
                            <span className="font-medium">
                              {categoriaSelecionada.descricao}
                            </span>
                            <span className="text-muted-foreground ml-2">
                              {categoriaSelecionada.ean}
                            </span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">
                            Selecione uma categoria
                          </span>
                        )}
                        <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="p-0 w-(--radix-popover-trigger-width)"
                    >
                      <Command className="rounded-lg">
                        <CommandInput placeholder="Buscar por descrição, código ou info. complementar..." />
                        <CommandList>
                          <CommandEmpty>
                            <div className="text-xs text-muted-foreground p-2">
                              Nenhuma categoria encontrada
                            </div>
                          </CommandEmpty>
                          <CommandGroup>
                            {categoriasOrdenadas.map((categoria, index) => (
                              <CommandItem
                                key={index}
                                value={categoriaComboValue(categoria)}
                                onSelect={(currentValue) => {
                                  const ean = currentValue
                                    .split("|-|")[0]
                                    .trim();
                                  const sel = categorias.find(
                                    (c) => c.ean === ean,
                                  );
                                  setCategoriaSelecionada(sel);
                                  setComboCodigo(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    categoriaSelecionada?.ean === categoria.ean
                                      ? "opacity-100"
                                      : "opacity-0",
                                  )}
                                />
                                <div className="flex flex-col min-w-0">
                                  <span className="text-sm font-medium truncate">
                                    {categoria.descricao}
                                  </span>
                                  <span className="text-[11px] text-muted-foreground">
                                    {categoria.ean}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                {epcsDaPlanilha.length === 0 ? (
                  <div
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (!isLoading) setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`flex flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed py-10 px-4 cursor-pointer transition-colors ${
                      isDragging
                        ? "border-primary bg-primary/5"
                        : "border-border bg-muted/20 hover:bg-muted/40 hover:border-primary/40"
                    } ${isLoading ? "pointer-events-none opacity-60" : ""}`}
                  >
                    <div
                      className={`flex h-12 w-12 items-center justify-center rounded-full transition-colors ${
                        isDragging ? "bg-primary/15" : "bg-muted"
                      }`}
                    >
                      <Upload
                        className={`h-6 w-6 ${
                          isDragging ? "text-primary" : "text-muted-foreground"
                        }`}
                      />
                    </div>
                    <div className="text-center space-y-1">
                      <p className="text-sm font-medium">
                        {isDragging
                          ? "Solte o arquivo aqui"
                          : "Arraste o arquivo ou clique para selecionar"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        <code className="px-1 py-0.5 rounded bg-muted text-[10px]">
                          .csv
                        </code>{" "}
                        ou{" "}
                        <code className="px-1 py-0.5 rounded bg-muted text-[10px]">
                          .txt
                        </code>{" "}
                        — 1 EPC por linha, ou CSV com coluna{" "}
                        <code className="px-1 py-0.5 rounded bg-muted text-[10px]">
                          TAG
                        </code>
                      </p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.txt"
                      className="sr-only"
                      onChange={handleFileInputChange}
                      disabled={isLoading}
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 p-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/10">
                        <FileSpreadsheet className="h-5 w-5 text-emerald-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {fileName || "Arquivo carregado"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {epcsDaPlanilha.length}{" "}
                          {epcsDaPlanilha.length === 1
                            ? "EPC importado"
                            : "EPCs importados"}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={limparPlanilha}
                      disabled={isLoading}
                    >
                      <X className="h-4 w-4" />
                      Limpar
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>
          </CardContent>
        </Card>
      </Tabs>

      {/* 3. Localizações */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
              3
            </div>
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              Localizações
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="grid gap-2">
              <Label
                htmlFor="gravacao-loc-1"
                className="text-xs text-muted-foreground"
              >
                Nível 1 <span className="text-destructive">*</span>
              </Label>
              <LocalizacaoCombobox
                id="gravacao-loc-1"
                disabled={isLoading}
                opcoes={localizacoes}
                value={localizacaoSelecionada}
                onChange={(value) => {
                  setLocalizacaoSelecionada(value);
                  setLocalizacaoSelecionada2("");
                  setLocalizacaoSelecionada3("");
                  setLocalizacaoSelecionada4("");
                }}
                placeholder="Não definido"
              />
            </div>
            <div className="grid gap-2">
              <Label
                htmlFor="gravacao-loc-2"
                className="text-xs text-muted-foreground"
              >
                Nível 2
              </Label>
              <LocalizacaoCombobox
                id="gravacao-loc-2"
                disabled={isLoading || !localizacaoSelecionada}
                opcoes={localizacoes
                  .find((n1) => n1._id === localizacaoSelecionada)
                  ?.children}
                value={localizacaoSelecionada2}
                onChange={(v) => {
                  setLocalizacaoSelecionada2(v);
                  setLocalizacaoSelecionada3("");
                  setLocalizacaoSelecionada4("");
                }}
                placeholder="Não definido"
              />
            </div>
            <div className="grid gap-2">
              <Label
                htmlFor="gravacao-loc-3"
                className="text-xs text-muted-foreground"
              >
                Nível 3
              </Label>
              <LocalizacaoCombobox
                id="gravacao-loc-3"
                disabled={
                  isLoading ||
                  !localizacaoSelecionada ||
                  !localizacaoSelecionada2
                }
                opcoes={localizacoes
                  .find((n1) => n1._id === localizacaoSelecionada)
                  ?.children.find((n2) => n2._id === localizacaoSelecionada2)
                  ?.children}
                value={localizacaoSelecionada3}
                onChange={(v) => {
                  setLocalizacaoSelecionada3(v);
                  setLocalizacaoSelecionada4("");
                }}
                placeholder="Não definido"
              />
            </div>
            <div className="grid gap-2">
              <Label
                htmlFor="gravacao-loc-4"
                className="text-xs text-muted-foreground"
              >
                Nível 4
              </Label>
              <LocalizacaoCombobox
                id="gravacao-loc-4"
                disabled={
                  isLoading ||
                  !localizacaoSelecionada ||
                  !localizacaoSelecionada2 ||
                  !localizacaoSelecionada3
                }
                opcoes={localizacoes
                  .find((n1) => n1._id === localizacaoSelecionada)
                  ?.children.find((n2) => n2._id === localizacaoSelecionada2)
                  ?.children.find((n3) => n3._id === localizacaoSelecionada3)
                  ?.children}
                value={localizacaoSelecionada4}
                onChange={setLocalizacaoSelecionada4}
                placeholder="Não definido"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 4. Preview dos EPCs da planilha */}
      {modo === "planilha" && epcsDaPlanilha.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                  4
                </div>
                <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                  <Layers className="h-4 w-4 text-muted-foreground" />
                  EPCs a gravar
                </CardTitle>
              </div>
              <Badge variant="secondary" className="font-medium">
                {epcsDaPlanilha.length}{" "}
                {epcsDaPlanilha.length === 1 ? "EPC" : "EPCs"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={[
                {
                  accessorKey: "epc",
                  header: "EPC",
                  cell: ({ row }) => (
                    <span className="text-xs">{row.original.epc}</span>
                  ),
                },
              ]}
              data={epcsDaPlanilha.map((epc) => ({ epc }))}
            />
          </CardContent>
        </Card>
      )}

      {/* AlertDialog: EPCs repetidos */}
      <AlertDialog
        open={epcRepetidosDialog}
        onOpenChange={setEpcRepetidosDialog}
      >
        <AlertDialogContent className="max-h-[90vh] overflow-hidden flex flex-col">
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500/10">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              </div>
              <AlertDialogTitle>EPCs repetidos encontrados</AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              Os EPCs abaixo já existem na base. Sincronize os dados e tente
              novamente, ou ative "Permitir repetidos" para gravar mesmo assim.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="overflow-y-auto max-h-64 rounded-md border">
            <ul className="divide-y">
              {epcRepetidosEncontrados.map((item, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between px-3 py-2 gap-2"
                >
                  <span className="text-xs truncate">{item.epc}</span>
                  <Badge variant="secondary" className="">
                    {item.codigo}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => setEpcRepetidosDialog(false)}>
              <Eye className="h-4 w-4" />
              Entendi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default GravacaoAvulsa;
